import { handleVariantDisplay } from '@app/pdp/components/ProductVariant/utils';
import { useProductDetails } from '@app/pdp/hooks';
import type { SelectedVariantKeys, UniqueVariants } from '@libs/algolia';
import { SelectedValueKey } from '@libs/algolia';
import { DefinitionList, RadioField, TextLink } from '@petsmart-ui/sparky';
import ProductVariantRadioField from './ProductVariantRadioField';

type ProductVariantRadioFieldsProps = {
    variants: Partial<UniqueVariants>;
    onChange?: (value: string, key: string) => void;
};

export default function ProductVariantRadioFields({
    variants,
    onChange,
}: ProductVariantRadioFieldsProps) {
    const {
        selectedProduct,
        selectedVariants,
        setSelectedVariants,
        product,
        setUserHasAddedToRecurringShipmentSuccessfully,
    } = useProductDetails();

    const sizeChartImg = selectedProduct.sizeChartImgUrl
        ? selectedProduct.sizeChartImgUrl
        : product.sizeChartImgUrl;

    const onClickSizeChart = () => {
        const sizeTabs = document.querySelectorAll(
            '.product-description [role="tab"]'
        );

        if (!sizeTabs) {
            return;
        }

        let sizeTabIndex;

        sizeTabs.forEach((tab, index) => {
            if (tab.textContent === 'Size') {
                sizeTabIndex = index;
            }
        });

        if (sizeTabIndex) {
            sizeTabs[sizeTabIndex]?.dispatchEvent(
                new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                })
            );
        }
    };

    const entries = Object.entries(variants);

    return (
        entries.length > 0 &&
        entries.map(([variantKey, entry]) => {
            const selectedValue =
                selectedVariants[variantKey as SelectedVariantKeys];

            return (
                <div className="size-variant__fields" key={variantKey}>
                    <DefinitionList
                        className="product-variant-descriptor"
                        variant="highlight-definition"
                    >
                        <DefinitionList.Item
                            className="size-variant__radio-fields-title"
                            data-testid={`variant-title-${variantKey}`}
                            title={`${handleVariantDisplay(variantKey)}:`}
                        >
                            {selectedValue}
                            {sizeChartImg &&
                                variantKey === SelectedValueKey.SIZE && (
                                    <TextLink
                                        href="#product-description"
                                        className="size-variant__size-char-link"
                                        onClick={onClickSizeChart}
                                        data-testid="size-chart-img-url-link"
                                    >
                                        Size chart
                                    </TextLink>
                                )}
                        </DefinitionList.Item>
                    </DefinitionList>
                    <RadioField
                        label="Choose size"
                        data-testid={`variant-selection-${variantKey}`}
                        variant="toggle"
                        selectedValue={selectedValue}
                        hideLegend
                        onChange={value => {
                            if (onChange) {
                                onChange(`${value}`, variantKey);
                                setUserHasAddedToRecurringShipmentSuccessfully(
                                    false
                                );
                            } else {
                                setSelectedVariants(prevState => ({
                                    ...prevState,
                                    [variantKey]: value,
                                }));
                            }
                        }}
                    >
                        {(entry.length > 1 ? entry : []).map(
                            ({ key, value }) => (
                                <ProductVariantRadioField
                                    key={key}
                                    variantKey={
                                        variantKey as SelectedVariantKeys
                                    }
                                    value={value}
                                    display={value}
                                />
                            )
                        )}
                    </RadioField>
                </div>
            );
        })
    );
}
